<template lang="pug">
  form(@submit.prevent="submit" novalidate)
    .modal-card
      header.modal-card-head
        p.modal-card-title(v-once) {{ title }}
      section.modal-card-body.modal-tabs
        b-tabs(
          position="centered"
          :animated="false"
          expanded
        )
          b-tab-item(label="Details")
            details-tab
          b-tab-item(label="Contact")
            contact-tab
          b-tab-item(label="Attachments" v-if="event.type === 'project'")
            attachments-tab
          b-tab-item(label="Artwork")
            cover-art-tab(
              v-model="artwork"
              :default-image="defaultImage"
              @input="a => artwork = a"
            )
          b-tab-item(label="Sign Up")
            sign-up-tab
      footer.modal-card-foot
        button.button(
          type="submit"
          :class="{ 'is-success': event.is_published, 'is-primary': !event.is_published, 'is-loading': settings.submitting }"
        ) {{ saveLabel }}
        a.button.is-danger(v-if="!isNew" @click="confirmDelete") Delete
</template>

<script>
import _ from 'lodash';
import moment from 'moment';
import { mapGetters } from 'vuex';
import AttachmentsTab from '@/components/form/event/AttachmentsTab';
import ContactTab from '@/components/form/event/ContactTab';
import CoverArtTab from '@/components/form/episode/CoverArtTab';
import DetailsTab from '@/components/form/event/DetailsTab';
import SignUpTab from '@/components/form/event/SignUpTab';
import * as actions from '@/store/types/actions';
import * as getters from '@/store/types/getters';
import * as mutations from '@/store/types/mutations';

export default {
  name: 'event-form',
  components: {
    AttachmentsTab,
    ContactTab,
    CoverArtTab,
    DetailsTab,
    SignUpTab,
  },
  computed: {
    ...mapGetters({
      event: getters.GET_EVENT,
    }),
    defaultImage() {
      const { artwork } = this.event._embedded;
      if (artwork) return artwork.file.url;
      return null;
    },
    isNew() {
      return this.event.id === undefined;
    },
    saveLabel() {
      return this.event.is_published ? `Publish ${this.typeLabel}` : 'Save Draft';
    },
    title() {
      return this.isNew ? `New ${this.typeLabel}` : `Edit ${this.typeLabel}: ${this.event.title}`;
    },
    typeLabel() {
      return this.$options.filters.capitalize(this.event.type);
    },
  },
  data() {
    return {
      artwork: null,
      edited: {
        artwork: false,
      },
      settings: {
        submitting: false,
      },
    };
  },
  methods: {
    confirmDelete() {
      this.$buefy.dialog.confirm({
        title: 'Are you sure?',
        message: 'Deleting this event cannot be undone',
        type: 'is-danger',
        hasIcon: true,
        confirmText: 'Yes, Delete Event',
        onConfirm: () => this.deleteEvent(),
      });
    },
    async deleteEvent() {
      const response = await this.$store.dispatch(actions.DO_DELETE_EVENT, {
        id: this.event.id,
      });
      this.$parent.close();
      this.$store.commit(mutations.SET_APP_SNACKBAR, {
        type: 'is-success',
        message: response.message,
      });
      setTimeout(() => {
        this.$store.commit(mutations.SET_EVENTS_REFRESH, true);
      }, 1000);
    },
    async savePackage(eventPackage, index, eventId) {
      const action = eventPackage.id === undefined ? actions.DO_CREATE_EVENT_PACKAGE : actions.DO_UPDATE_EVENT_PACKAGE;
      const payload = {
        id: eventPackage.id || eventId,
        name: eventPackage.name,
        cost: eventPackage.cost,
        discount_cost: eventPackage.discount_cost,
        sort_order: index,
      };
      const { data } = await this.$store.dispatch(action, payload);
      this.$store.commit(mutations.SET_PACKAGE_FIELD_FOR_EVENT, {
        key: 'id',
        index,
        val: data.packages[0].id,
      });
      return data;
    },
    async savePackages(packages, eventId) {
      const save = [];
      let i = 0;
      packages.forEach((p) => {
        save.push(this.savePackage(p, i, eventId));
        i += 1;
      });
      await Promise.all(save);
    },
    async submit() {
      const valid = await this.$validator.validateAll();
      if (valid) {
        this.settings.submitting = true;
        const action = this.isNew ? actions.DO_CREATE_EVENT : actions.DO_OVERRIDE_EVENT;
        const params = {
          id: this.event.id,
          cost: this.event.cost,
          discount_cost: this.event.discount_cost,
          ignore: true,
          is_published: this.event.is_published,
          key: this.event.key,
          long_desc: this.event.long_desc,
          notification_emails: this.event.notification_emails,
          payment_required: this.event.payment_required,
          registration_deadline: this.event.registration_deadline,
          registration_required: this.event.registration_required,
          short_desc: this.event.short_desc,
          sort_order: this.event.sort_order,
          title: this.event.title,
          type: this.event.type,
          zoom: 'artwork,contact,location,packages',
        };
        if (this.event.is_published) {
          params.pub_date = moment(this.event.pub_date).format('YYYY-MM-DD');
        }
        const joinFields = [
          'collection_ids',
          'dates',
          'event_ids',
          'ministry_ids',
          'notification_emails',
        ];
        joinFields.forEach((field) => {
          if (_.isArray(this.event[field])) {
            params[field] = this.event[field].join(',');
          }
        });
        const timestamps = [
          'end_time',
          'start_time',
        ];
        timestamps.forEach((timestamp) => {
          if (this.event[timestamp]) {
            const format = 'YYYY-MM-DD HH:mm Z';
            params[timestamp] = moment(this.event[timestamp]).format(format);
          }
        });
        if (this.event._embedded.location.id) {
          params.location_id = this.event._embedded.location.id;
        }
        if (this.artwork) {
          const file = this.artwork.generateDataUrl('image/jpeg');
          const artworkResponse = await this.$store.dispatch(actions.DO_CREATE_MEDIA_IMAGE, { file });
          params.artwork_id = artworkResponse.data.images[0].id;
        } else {
          params.artwork_id = this.event.artwork_id;
        }
        if (this.event._embedded.contact.name) {
          const { contact } = this.event._embedded;
          const contactAction = contact.id === undefined ? actions.DO_CREATE_EVENT_CONTACT : actions.DO_UPDATE_EVENT_CONTACT;
          const contactResponse = await this.$store.dispatch(contactAction, {
            id: contact.id,
            email: contact.email,
            name: contact.name,
            phone_number: contact.phone_number,
            staff_member_id: contact.staff_member_id,
          });
          params.contact_id = contactResponse.data.event_contacts[0].id;
          this.$store.commit(mutations.SET_EVENT_FIELD, {
            key: '_embedded.contact',
            val: contactResponse.data.event_contacts[0],
          });
        }
        const packages = this.event.payment_required ? _.cloneDeep(this.event._embedded.packages) : [];
        try {
          const response = await this.$store.dispatch(action, params);
          // this.$store.commit(mutations.SET_EVENT_FIELD, {
          //   key: 'id',
          //   val: ,
          // });
          if (this.event.payment_required) {
            await this.savePackages(packages, response.data.events[0].id);
          }
          setTimeout(() => {
            this.$parent.close();
            this.settings.submitting = false;
            this.$store.commit(mutations.SET_APP_SNACKBAR, {
              type: 'is-success',
              message: response.message,
            });
            this.$store.commit(mutations.SET_EVENTS_REFRESH, true);
          }, 1000);
        } catch (error) {
          this.settings.submitting = false;
          this.$store.commit(mutations.SET_APP_SNACKBAR, {
            type: 'is-danger',
            message: error.response.data.data.error_message,
          });
        }
      }
    },
  },
};
</script>

<style lang="scss">

</style>



// WEBPACK FOOTER //
// src/components/form/EventForm.vue